import { Bars3Icon, ChevronDownIcon } from '@heroicons/react/24/solid';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Slot } from '@radix-ui/react-slot';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { ScrollArea } from '../ui/scroll-area';
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { Separator } from '../ui/separator';
import { CustomNavLinkExternal } from '../custom-navlink-external';
import { CustomNavLink } from '../custom-navlink';
import { cn } from '@/lib/utils';
import type { PrimaryNavigationItem } from '@/services/cms/getPrimaryNavigation/schemas';
import { LoginSheet } from '../sheets/login-sheets';
import { UserProfile } from '../user-profile';
import { AgentProfile } from '../agent-profile';
import { onClickNavExternal, onClickNavInternal } from '@/utils/navigation';
import { PRIMARY_NAV_TYPE } from '@/constants/app.constants';
import { DynamicHeroIcon } from '../dynamic-heroicon';
import { useState } from 'react';
interface AppSidebarProps {
  navigationData: PrimaryNavigationItem[];
}
export const AppSidebar = ({ navigationData }: AppSidebarProps) => {
  const [isSideBarOpen, setIsSideBarOpen] = useState(false);

  return (
    <Sheet open={isSideBarOpen}>
      <SheetTrigger
        asChild
        onClick={() => {
          setIsSideBarOpen(true);
        }}
      >
        <Bars3Icon className="text-black size-6" />
      </SheetTrigger>
      <SheetContent
        onInteractOutside={(e) => {
          e.preventDefault();
          setIsSideBarOpen(false);
        }}
      >
        <VisuallyHidden.Root>
          <SheetTitle>AppSidebar Navigation</SheetTitle>
          <SheetDescription>Navigation items</SheetDescription>
        </VisuallyHidden.Root>
        <ScrollArea className="flex-1 p-2">
          {navigationData.map((item, index) => (
            <Collapsible
              key={item.page.id}
              title={item.page.title}
              defaultOpen={false}
              className="group/collapsible"
            >
              <div
                data-sidebar="group"
                className="relative flex w-full min-w-0 flex-col p-2"
              >
                <Slot
                  data-sidebar="group-label"
                  className="duration-200 flex h-8 shrink-0 items-center rounded-lg px-2 outline-hidden transition-[margin,opa] ease-linear [&>svg]:size-4 md:[&>svg]:size-5 [&>svg]:shrink-0 group/label text-xs md:text-sm font-nunito700 text-black group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0"
                >
                  {!item.children.length ? (
                    <>
                      {item.page.new_window ? (
                        <CustomNavLinkExternal
                          onClick={() => {
                            onClickNavExternal({
                              title: item.page.title,
                              url: item.page.url,
                              navType: PRIMARY_NAV_TYPE,
                            });
                          }}
                          href={item.page.url}
                          key={item.page.id}
                          className="text-xs md:text-sm font-nunito700 text-black"
                        >
                          <div className="flex gap-[10px] justify-between md:gap-[20px] hover:bg-warp-green-400 rounded-lg p-2 md:py-[10px]">
                            {item.page.title}
                            {item.page.icon && (
                              <DynamicHeroIcon
                                iconName={item.page.icon}
                                shape={item.page.icon_style?.key ?? 'outline'}
                                className="size-[20px] shrink-0"
                              />
                            )}
                          </div>
                        </CustomNavLinkExternal>
                      ) : (
                        <CustomNavLink
                          onClick={() => {
                            setIsSideBarOpen(false);
                            onClickNavInternal({
                              title: item.page.title,
                              url: item.page.url,
                              navType: PRIMARY_NAV_TYPE,
                            });
                          }}
                          to={item.page.url}
                          className="text-xs md:text-sm font-nunito700 text-black"
                          search
                          key={item.page.id}
                          activeOptions={{ exact: true }}
                        >
                          {({ isActive }) => (
                            <div
                              className={cn(
                                'flex gap-[10px] md:gap-[20px] hover:bg-warp-green-400 rounded-lg p-2 md:py-[10px]',
                                {
                                  'bg-warp-green-400': isActive,
                                }
                              )}
                            >
                              {item.page.title}
                            </div>
                          )}
                        </CustomNavLink>
                      )}
                    </>
                  ) : (
                    <CollapsibleTrigger>
                      {item.page.title}{' '}
                      <ChevronDownIcon className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                    </CollapsibleTrigger>
                  )}
                </Slot>

                <CollapsibleContent>
                  <div data-sidebar="group-content" className="w-full text-sm">
                    <ul
                      data-sidebar="menu"
                      className="flex w-full min-w-0 flex-col md:gap-1 gap-[2.7px]"
                    >
                      {item.children.map((subItem) => (
                        <li
                          data-sidebar="menu-item"
                          key={subItem.page.id}
                          className="group/menu-item relative"
                        >
                          {subItem.page.new_window ? (
                            <CustomNavLinkExternal
                              onClick={() => {
                                onClickNavExternal({
                                  title: subItem.page.title,
                                  url: subItem.page.url,
                                  navType: PRIMARY_NAV_TYPE,
                                });
                              }}
                              href={subItem.page.url}
                              key={subItem.page.id}
                              className="text-xs md:text-sm font-nunito700 text-black"
                            >
                              <div className="flex gap-[10px] md:gap-[20px] hover:bg-warp-green-400 rounded-lg p-2 md:pr-5 md:py-[10px]">
                                {subItem.page.title}
                              </div>
                            </CustomNavLinkExternal>
                          ) : (
                            <CustomNavLink
                              onClick={() => {
                                setIsSideBarOpen(false);
                                onClickNavInternal({
                                  title: subItem.page.title,
                                  url: subItem.page.url,
                                  navType: PRIMARY_NAV_TYPE,
                                });
                              }}
                              to={subItem.page.url}
                              className="text-xs md:text-sm font-nunito700 text-black"
                              search
                              key={subItem.page.id}
                              activeOptions={{ exact: true }}
                            >
                              {({ isActive }) => (
                                <div
                                  className={cn(
                                    'flex gap-[10px] md:gap-[20px] hover:bg-warp-green-400 rounded-lg p-2 md:pr-5 md:py-[10px]',
                                    {
                                      'bg-warp-green-400': isActive,
                                    }
                                  )}
                                >
                                  {subItem.page.title}
                                </div>
                              )}
                            </CustomNavLink>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CollapsibleContent>
              </div>
              {index < navigationData.length - 1 ? (
                <Separator className="bg-grey-50" />
              ) : null}
            </Collapsible>
          ))}

          <Separator className="bg-grey-50" />
          <AgentProfile />
          <UserProfile />
          <LoginSheet className="p-2 pl-3 md:px-5 md:pl-3 md:py-[10px] mt-2 hover:bg-warp-green-400 w-full" />
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
